import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { AuthHero } from "@/components/AuthHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mail, Phone, Lock, User, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const Signup = () => {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("email");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { name: form.name, password: form.password };
      if (tab === "email") payload.email = form.email; else payload.mobile = form.mobile;
      await signup(payload);
      toast.success("Account created!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      <AuthHero
        accent="Start your ride!"
        heading={<>Join 2.4M riders.<br />Two minutes flat.</>}
        sub="Free first-ride helmet upgrade, loyalty cashback on every booking, priority pickup at peak hours."
      />

      <div className="flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden block mb-8"><Logo /></Link>

          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Create your account</h1>
          <p className="text-gray-600 mt-2">Choose how you'd like to sign up.</p>

          <Tabs value={tab} onValueChange={setTab} className="mt-7">
            <TabsList className="grid grid-cols-2 w-full bg-gray-100 rounded-full p-1 h-auto">
              <TabsTrigger value="email" data-testid="signup-tab-email" className="rounded-full data-[state=active]:bg-white py-2.5">
                <Mail size={14} className="mr-2" />Email
              </TabsTrigger>
              <TabsTrigger value="mobile" data-testid="signup-tab-mobile" className="rounded-full data-[state=active]:bg-white py-2.5">
                <Phone size={14} className="mr-2" />Mobile
              </TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="signup-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aarav Mehta" className="pl-10 h-12 rounded-xl" />
                </div>
              </div>

              <TabsContent value="email" className="space-y-2 m-0">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="signup-email-input" type="email" required={tab === "email"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="pl-10 h-12 rounded-xl" />
                </div>
              </TabsContent>
              <TabsContent value="mobile" className="space-y-2 m-0">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Mobile</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="signup-mobile-input" type="tel" required={tab === "mobile"} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 98765 43210" className="pl-10 h-12 rounded-xl" />
                </div>
              </TabsContent>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input data-testid="signup-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="pl-10 h-12 rounded-xl" />
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="signup-submit-btn" className="btn-primary w-full justify-center !py-3.5 !text-base">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <><span>Create account</span><ArrowRight size={16} /></>}
              </button>
            </form>
          </Tabs>

          <p className="text-center text-sm text-gray-600 mt-7">
            Already a rider?{" "}
            <Link to="/login" data-testid="goto-login-link" className="font-bold text-[#0d8f5b] hover:text-[#2D7FF9]">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
